import React, { useState } from 'react';
import { HealthCheckinModal } from './HealthCheckinModal';

// Demo data for Health Labs showcase
const DEMO_EXPERIMENTS = {
  nutrition: [
    {
      id: 'post-workout-protein',
      title: 'Post-Workout Protein Timing',
      description: 'Test if eating protein within 30 minutes after workouts improves your recovery and energy levels.',
      status: 'active',
      progress: 71,
      daysLeft: 2,
      insights: ['40% higher energy when protein consumed within 2 hours of workout']
    },
    {
      id: 'hydration-optimization',
      title: 'Hydration Sweet Spot',
      description: 'Find your optimal daily water intake for peak energy and performance.',
      status: 'not_started',
      progress: 0,
      daysLeft: 14,
      insights: []
    }
  ],
  sleep: [
    {
      id: 'wind-down-routine',
      title: '3-2-1 Wind-Down Rule',
      description: 'Test the 3-2-1 rule: No food 3hrs before bed, no work 2hrs before, no screens 1hr before.',
      status: 'active',
      progress: 85,
      daysLeft: 2,
      insights: ['18 minutes faster sleep when following 3-2-1 rule']
    },
    {
      id: 'sleep-environment',
      title: 'Optimal Sleep Environment',
      description: 'Experiment with room temperature, darkness, and noise levels to find your perfect sleep setup.',
      status: 'completed',
      progress: 100,
      daysLeft: 0,
      insights: [
        'You sleep best at 67°F with complete darkness',
        'White noise improved sleep quality by 23%',
        'Blackout curtains more effective than eye mask'
      ]
    }
  ]
};

const DEMO_INSIGHTS = {
  nutrition: [
    {
      title: 'Protein Timing Boosts Your Energy',
      description: 'You have 40% higher energy levels when you eat protein within 2 hours of waking up.',
      confidence: 87,
      category: 'pattern'
    },
    {
      title: 'Your Hydration Sweet Spot: 9 Glasses',
      description: 'Your workout performance peaks when you drink 9 glasses of water daily.',
      confidence: 92,
      category: 'discovery'
    }
  ],
  sleep: [
    {
      title: 'Cool Rooms = Better Sleep',
      description: 'You sleep 30% better when your room is 67°F or cooler.',
      confidence: 89,
      category: 'pattern'
    },
    {
      title: 'Screen-Free Hour = Faster Sleep',
      description: 'When you avoid screens 1 hour before bed, you fall asleep 18 minutes faster.',
      confidence: 94,
      category: 'discovery'
    }
  ]
};

const DEMO_CHECKIN_PROMPTS = {
  nutrition: [
    {
      id: 'energy-level',
      question: 'How is your energy level right now?',
      type: 'scale' as const,
      min: 1,
      max: 5,
      options: ['Very Low', 'Very High']
    },
    {
      id: 'post-workout-protein',
      question: 'Did you eat protein within 30 minutes after your workout?',
      type: 'boolean' as const
    },
    {
      id: 'protein-source',
      question: 'What protein source did you choose?',
      type: 'tags' as const,
      options: ['Protein Shake', 'Greek Yogurt', 'Eggs', 'Chicken', 'Fish', 'Beans', 'Other']
    }
  ],
  sleep: [
    {
      id: 'bedtime',
      question: 'What time did you go to bed last night?',
      type: 'time' as const
    },
    {
      id: 'sleep-quality',
      question: 'How would you rate your sleep quality?',
      type: 'scale' as const,
      min: 1,
      max: 5,
      options: ['Very Poor', 'Excellent']
    },
    {
      id: 'morning-energy',
      question: 'How is your energy level this morning?',
      type: 'scale' as const,
      min: 1,
      max: 5,
      options: ['Very Low', 'Very High']
    }
  ]
};

export const HealthLabsDemo: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'nutrition' | 'sleep'>('nutrition');
  const [selectedExperiment, setSelectedExperiment] = useState<string | null>(null);
  const [showInsights, setShowInsights] = useState(false);
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [checkinType, setCheckinType] = useState<'nutrition' | 'sleep'>('nutrition');

  const handleStartCheckin = (type: 'nutrition' | 'sleep') => {
    setCheckinType(type);
    setShowCheckinModal(true);
  };

  const handleCheckinSubmit = (responses: any[]) => {
    console.log('Check-in completed:', responses);
    setShowCheckinModal(false);
    alert(`${checkinType.charAt(0).toUpperCase() + checkinType.slice(1)} check-in completed! 🎉`);
  };

  const getSectionIcon = (section: 'nutrition' | 'sleep') => {
    return section === 'nutrition' ? '🧪' : '🌙';
  };

  const getSectionTitle = (section: 'nutrition' | 'sleep') => {
    return section === 'nutrition' ? 'Nutrition Lab' : 'Sleep Studio';
  };

  const experiments = DEMO_EXPERIMENTS[activeSection];
  const insights = DEMO_INSIGHTS[activeSection];
  const activeExperiment = experiments.find(exp => exp.status === 'active');

  // Insights view
  if (showInsights) {
    return (
      <div className="max-w-md mx-auto bg-white min-h-screen">
        <div className="p-4">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => setShowInsights(false)}
              className="text-gray-600 hover:text-gray-800"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Your Health Intelligence</h1>
          </div>

          {/* Health Intelligence Score */}
          <div className="bg-gradient-to-r from-purple-500 to-blue-600 rounded-xl p-6 text-white mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">Health Intelligence Score</h3>
                <p className="text-purple-100 text-sm">Based on your discoveries</p>
              </div>
              <div className="text-3xl font-bold">8.7</div>
            </div>
            <div className="w-full bg-purple-300 rounded-full h-2">
              <div className="bg-white h-2 rounded-full" style={{ width: '87%' }}></div>
            </div>
            <p className="text-purple-100 text-xs mt-2">You've discovered 12 personal health patterns</p>
          </div>

          {/* Insights List */}
          <div className="space-y-4">
            {[...DEMO_INSIGHTS.nutrition, ...DEMO_INSIGHTS.sleep].map((insight, index) => (
              <div key={index} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      insight.category === 'pattern' ? 'bg-blue-500' : 'bg-green-500'
                    }`}></span>
                    <span className="text-xs font-medium text-gray-500 uppercase">
                      {insight.category}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500">{insight.confidence}%</span>
                    <div className="w-8 bg-gray-200 rounded-full h-1">
                      <div 
                        className="bg-green-500 h-1 rounded-full"
                        style={{ width: `${insight.confidence}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{insight.title}</h3>
                <p className="text-sm text-gray-600">{insight.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Experiment detail view
  if (selectedExperiment) {
    const experiment = [...DEMO_EXPERIMENTS.nutrition, ...DEMO_EXPERIMENTS.sleep]
      .find(exp => exp.id === selectedExperiment);
    
    if (!experiment) return null;

    return (
      <div className="max-w-md mx-auto bg-white min-h-screen">
        <div className="p-4">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => setSelectedExperiment(null)}
              className="text-gray-600 hover:text-gray-800"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900">{experiment.title}</h1>
          </div>

          <div className="space-y-6">
            {/* Status */}
            <div className={`rounded-lg p-4 ${
              experiment.status === 'active' ? 'bg-blue-50 border border-blue-200' :
              experiment.status === 'completed' ? 'bg-green-50 border border-green-200' :
              'bg-gray-50 border border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  experiment.status === 'active' ? 'bg-blue-100 text-blue-800' :
                  experiment.status === 'completed' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {experiment.status === 'active' ? 'Active Experiment' :
                   experiment.status === 'completed' ? 'Completed' : 'Ready to Start'}
                </span>
                {experiment.daysLeft > 0 && (
                  <span className="text-sm text-gray-600">{experiment.daysLeft} days left</span>
                )}
              </div>
              
              {experiment.progress > 0 && (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Progress</span>
                    <span className="text-sm text-gray-600">{experiment.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        experiment.status === 'completed' ? 'bg-green-600' : 'bg-blue-600'
                      }`}
                      style={{ width: `${experiment.progress}%` }}
                    ></div>
                  </div>
                </>
              )}
            </div>

            {/* Description */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">About This Experiment</h3>
              <p className="text-gray-700">{experiment.description}</p>
            </div>

            {/* Insights */}
            {experiment.insights.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Discoveries</h3>
                <div className="space-y-3">
                  {experiment.insights.map((insight, index) => (
                    <div key={index} className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <div className="flex items-start gap-2">
                        <span className="text-lg">🎉</span>
                        <p className="text-sm text-purple-800 font-medium">{insight}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {experiment.status === 'not_started' && (
                <button className="w-full bg-green-700 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-800 transition-colors">
                  Start This Experiment
                </button>
              )}
              
              {experiment.status === 'active' && (
                <button 
                  onClick={() => handleStartCheckin(experiment.id.includes('nutrition') ? 'nutrition' : 'sleep')}
                  className="w-full bg-blue-700 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-800 transition-colors"
                >
                  Today's Check-in
                </button>
              )}

              {experiment.status === 'completed' && (
                <button className="w-full bg-purple-700 text-white py-3 px-4 rounded-lg font-medium hover:bg-purple-800 transition-colors">
                  View Full Results
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Health Labs view
  return (
    <div className="max-w-md mx-auto bg-white min-h-screen">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Health Labs</h1>
          <button
            onClick={() => setShowInsights(true)}
            className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium hover:bg-purple-200 transition-colors"
          >
            My Insights
          </button>
        </div>
        
        {/* Section Tabs */}
        <div className="flex space-x-1 mb-6">
          {(['nutrition', 'sleep'] as const).map((section) => (
            <button
              key={section}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeSection === section 
                  ? section === 'nutrition' 
                    ? 'bg-green-700 text-white' 
                    : 'bg-blue-700 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setActiveSection(section)}
            >
              <span>{getSectionIcon(section)}</span>
              {getSectionTitle(section)}
            </button>
          ))}
        </div>

        {/* Active Experiment Card */}
        {activeExperiment && (
          <div className={`rounded-xl p-4 border-2 mb-6 ${
            activeSection === 'nutrition' 
              ? 'bg-green-50 border-green-200' 
              : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">{getSectionIcon(activeSection)}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                activeSection === 'nutrition' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                Active Experiment
              </span>
            </div>
            <h3 className={`font-semibold mb-2 ${
              activeSection === 'nutrition' ? 'text-green-900' : 'text-blue-900'
            }`}>
              {activeExperiment.title}
            </h3>
            <p className={`text-sm mb-3 ${
              activeSection === 'nutrition' ? 'text-green-800' : 'text-blue-800'
            }`}>
              {activeExperiment.description}
            </p>
            
            <div className="flex items-center justify-between mb-3">
              <span className={`text-sm font-medium ${
                activeSection === 'nutrition' ? 'text-green-900' : 'text-blue-900'
              }`}>
                Progress: {activeExperiment.progress}%
              </span>
              <span className={`text-xs ${
                activeSection === 'nutrition' ? 'text-green-700' : 'text-blue-700'
              }`}>
                {activeExperiment.daysLeft} days left
              </span>
            </div>
            
            <div className={`w-full rounded-full h-2 mb-4 ${
              activeSection === 'nutrition' ? 'bg-green-200' : 'bg-blue-200'
            }`}>
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  activeSection === 'nutrition' ? 'bg-green-600' : 'bg-blue-600'
                }`}
                style={{ width: `${activeExperiment.progress}%` }}
              ></div>
            </div>
            
            <div className="flex gap-2">
              <button 
                onClick={() => handleStartCheckin(activeSection)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  activeSection === 'nutrition' 
                    ? 'bg-green-700 text-white hover:bg-green-800' 
                    : 'bg-blue-700 text-white hover:bg-blue-800'
                }`}
              >
                Today's Check-in
              </button>
              <button 
                onClick={() => setSelectedExperiment(activeExperiment.id)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSection === 'nutrition' 
                    ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                    : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                }`}
              >
                Details
              </button>
            </div>
          </div>
        )}

        {/* Recent Insights */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Recent Discoveries</h3>
            <button
              onClick={() => setShowInsights(true)}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              View All
            </button>
          </div>
          <div className="space-y-3">
            {insights.slice(0, 2).map((insight, index) => (
              <div key={index} className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-start gap-2 mb-2">
                  <span className="text-lg">💡</span>
                  <div className="flex-1">
                    <h4 className="font-medium text-purple-900 text-sm">{insight.title}</h4>
                    <p className="text-purple-800 text-xs mt-1">{insight.description}</p>
                  </div>
                  <span className="text-xs text-purple-600">{insight.confidence}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Available Experiments */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Available Experiments</h3>
          <div className="space-y-3">
            {experiments.filter(exp => exp.status !== 'active').map((experiment) => (
              <div
                key={experiment.id}
                className="bg-white border rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
                onClick={() => setSelectedExperiment(experiment.id)}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    experiment.status === 'completed' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {experiment.status === 'completed' ? 'Completed' : 'New'}
                  </span>
                  <span className="text-xs text-gray-500">{experiment.daysLeft} days</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">{experiment.title}</h4>
                <p className="text-sm text-gray-600 mb-2">{experiment.description}</p>
                
                {experiment.status === 'completed' && experiment.insights.length > 0 && (
                  <div className="bg-green-50 rounded-lg p-2 mt-2">
                    <p className="text-xs text-green-800 font-medium">
                      ✨ {experiment.insights.length} insights discovered
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Health Check-in Modal */}
      <HealthCheckinModal
        isOpen={showCheckinModal}
        onClose={() => setShowCheckinModal(false)}
        experimentTitle={`${checkinType.charAt(0).toUpperCase() + checkinType.slice(1)} Check-in`}
        experimentType={checkinType}
        checkinPrompts={DEMO_CHECKIN_PROMPTS[checkinType]}
        onSubmit={handleCheckinSubmit}
      />
    </div>
  );
}; 